import { defineStore } from "pinia";
import { millisecondsInSecond } from "date-fns/constants";
import { useAccess } from "@/composables/useAccess";
import { useApiVersion } from "@/composables/useApiVersion";
import { useBcd } from "@/composables/useBcd";
import { useByteUtils } from "@/composables/useByteUtils";
import { useModbusAddress } from "@/composables/useModbusAddress";
import { useModbusWorker } from "@/composables/useModbusWorker";
import { useParams } from "@/composables/useParams";
import { useUnits } from "@/composables/useUnits";
import { useDelay } from "@/composables/useDelay";
import { useUtils } from "@/composables/useUtils";
import { AccessLevel } from "@/enums/AccessLevel";
import { AccessLevelSetting } from "@/enums/AccessLevelSetting";
import { ParamField } from "@/enums/ParamField";
import { PortState } from "@/enums/PortState";
import { allSensorCodes } from "@/lib/sensors";
import { DataType } from "@/types/DataType";
import { LabelSeries } from "@/types/LabelSeries";
import { Logger } from "@/types/Logger";
import { ModbusParam } from "@/types/ModbusParam";
import { ModbusStore } from "@/types/ModbusStore";
import { DataSeries } from "@/types/graph/DataSeries";
import { SensorCode } from "@/types/sensor/SensorCode";

const { getParamReadAccessLevel, getParamWriteAccessLevel } = useAccess();
const { apiVersion } = useApiVersion();
const { bcdArrayToVersionString } = useBcd();
const {
  bytes_to_date_time,
  bytes_to_float,
  bytes_to_int32,
  bytes_to_uint16,
  bytes_to_uint32,
  uint16_to_bytes,
} = useByteUtils();
const { modbusAddress } = useModbusAddress();
const { getModbusWorker, resetModbusWorker } = useModbusWorker();
const { getDeviceUnitsCsv, getUnitsGuiCsv } = useUnits();
const { isNullOrUndefined } = useUtils();
const { timeout } = useDelay();

// These addresses are indentical regardless of API version.
// Copy them to here for quick reference
const {
  softwareVersion: softwareVersionAddr,
  hardwareVersion: hardwareVersionAddr,
  parameterVersion: parameterVersionAddr,
} = modbusAddress;

let modbusWorker = getModbusWorker();

// Workaround because injecting a provided object is not available outside a vue component.
// We would need 'setup' syntax for the store, see https://tinyurl.com/vue-store-function-syntax
// We could then use inject() inside defineStore()
//const $log = inject<Logger>("vuejs3-logger");
const $log: Logger = {
  ...console,
  // eslint-disable-next-line no-console
  fatal: console.error,
};

const emptyLabelSeries: LabelSeries = {
  demoData: [],
  pollingData: [],
};

const emptyDataSeries = allSensorCodes.reduce(
  (acc: DataSeries, name: SensorCode) => {
    acc[name] = {
      name,
      color: "#000",
      demoData: [],
      pollingData: [],
    };
    return acc;
  },
  {} as DataSeries,
);

export const useModbusStore = defineStore("modbusStore", {
  state: (): ModbusStore => ({
    storeInit: false,
    portIndex: -1,
    portState: PortState.disconnected,
    communicationScore: 0,
    deviceAddress: 1,
    params: [],
    writeableParams: [],
    quickParams: [],
    selectedQuickParams: {},
    deviceUnits: [],
    guiUnits: [],
    maxReloadCount: 5,
    modbusValues: {},
    modbusIsChanged: false,
    modbusAddrParams: {},
    modbusAddrValue: {},
    modbusAddrDefaultValue: {},
    modbusAddrLength: {},
    modbusAddrReadBusy: {},
    modbusAddrWriteBusy: {},
    modbusAddrErrMsg: {},
    modbusRefUnit: {},
    accessLevel: AccessLevel.normal,
    groups: [],
    subgroups: {},
    previousGroup: "",
    previousSubgroup: "",
    maxTime: 240, //500 seconds
    isPollingMode: false,
    pollingFlag: false,
    pollingTimer: 0,
    isDemoMode: false,
    demoFlag: false,
    demoTimer: 0,
    labelSeries: emptyLabelSeries,
    dataSeries: emptyDataSeries,
    logToggleZoom: null,
    logDateState: 0,
    log7dMinTime: 0,
    log7dMaxTime: 0,
    slider_7d: [0, 100],
    slider_1m: [0, 100],
    slider_1s: [0, 100],
    slider_100ms: [0, 100],
    log1mDataIsOk: false,
    log1mDataTime: [],
    log1mDataRP: [],
    log1mDataS1: [],
    log1mDataS2: [],
    log1mDataM12: [],
    log1mDataSetpoint: [],
    log1mDataV1: [],
    log1mDataV2: [],
    log1mDataTemp: [],
    log1mDataBusy: false,
    log1sDataIsOk: false,
    log1sDataTime: [],
    log1sDataRP: [],
    log1sDataS1: [],
    log1sDataS2: [],
    log1sDataM12: [],
    log1sDataSetpoint: [],
    log1sDataV1: [],
    log1sDataV2: [],
    log1sDataTemp: [],
    log1sDataBusy: false,
    log100msDataIsOk: false,
    log100msDataTime: [],
    log100msDataRP: [],
    log100msDataS1: [],
    log100msDataS2: [],
    log100msDataM12: [],
    log100msDataSetpoint: [],
    log100msDataV1: [],
    log100msDataV2: [],
    log100msDataTemp: [],
    log100msDataBusy: false,
  }),
  actions: {
    // will be called when app is mounted - App.vue/onMounted()
    async initLoad() {
      // getting infos from csv files
      // from ./data/params-{version}-GUI.csv
      if (!(await this.initLoadParams())) {
        return;
      }

      // from ./data/device_units.csv
      const deviceUnits = await getDeviceUnitsCsv();
      if (deviceUnits === null) {
        return null;
      }
      this.deviceUnits = deviceUnits;

      // from ./data/units_gui.csv
      const guiUnits = await getUnitsGuiCsv();
      if (guiUnits === null) {
        return null;
      }
      this.guiUnits = guiUnits;

      await this.initGroups();
      await this.initWriteableParams();

      this.storeInit = true;
    },

    async initLoadParams(
      version?: string,
      deviceIdentification?: string,
    ): Promise<boolean> {
      const { getParamsCsv } = useParams($log);
      const paramsfile = await getParamsCsv(version, deviceIdentification);
      if (paramsfile === null) {
        return false;
      }
      this.params = paramsfile
        .filter((item: ModbusParam) => item.name !== "0" && item.type !== "0")
        .map((item: ModbusParam) => {
          if (ParamField.readOnly in item) {
            // pesky Excel keeps converting booleans to uppercase when saving csv
            return {
              ...item,
              [ParamField.readOnly]: item[ParamField.readOnly].toLowerCase(),
            };
          }
          return item;
        });
      return true;
    },

    async initGroups() {
      // Get groups & subgroups. These will be used in the settings view
      let char2MbusAddr = "";
      this.groups = [];
      this.subgroups = {};
      for (let i = 0; i < this.params.length; i++) {
        const param = this.params[i];
        if (param[ParamField.mbusAddress] === "") {
          continue;
        }
        const group = param[ParamField.group];
        const subGroup = param[ParamField.subGroup];
        if (group && group !== "0" && !this.groups.find((g) => g === group)) {
          this.groups.push(group);
        }
        if (group && group !== "0" && subGroup && subGroup !== "0") {
          if (!this.subgroups[group]) {
            this.subgroups[group] = [subGroup];
          } else {
            if (!this.subgroups[group].find((g) => g === subGroup)) {
              this.subgroups[group].push(subGroup);
            }
          }
        }
        // status variables : addr -> status
        this.modbusAddrParams[param[ParamField.mbusAddress]] = param;
        this.modbusAddrReadBusy[param[ParamField.mbusAddress]] = false;
        this.modbusAddrWriteBusy[param[ParamField.mbusAddress]] = false;
        this.modbusAddrErrMsg[param[ParamField.mbusAddress]] = "empty data";
        switch (param[ParamField.type]) {
          case "bool":
            this.modbusAddrLength[param[ParamField.mbusAddress]] = 1;
            this.modbusAddrDefaultValue[param[ParamField.mbusAddress]] =
              parseInt(param[ParamField.default]) === 1;
            break;
          case "uint16_t":
            this.modbusAddrLength[param[ParamField.mbusAddress]] = 1;
            this.modbusAddrDefaultValue[param[ParamField.mbusAddress]] =
              parseInt(param[ParamField.default]);
            break;
          case "uint32_t":
            this.modbusAddrLength[param[ParamField.mbusAddress]] = 2;
            this.modbusAddrDefaultValue[param[ParamField.mbusAddress]] =
              parseInt(param[ParamField.default]);
            break;
          case "float":
            this.modbusAddrLength[param[ParamField.mbusAddress]] = 2;
            this.modbusAddrDefaultValue[param[ParamField.mbusAddress]] =
              parseFloat(param[ParamField.default]);
            break;
          case "int":
            this.modbusAddrLength[param[ParamField.mbusAddress]] = 2;
            this.modbusAddrDefaultValue[param[ParamField.mbusAddress]] =
              parseInt(param[ParamField.default]);
            break;
          case "char2":
            if (param[ParamField.name].indexOf("[0:1]") > 0) {
              char2MbusAddr = param[ParamField.mbusAddress];
              this.modbusAddrLength[char2MbusAddr] = 1;
              if (param[ParamField.default].trim() === "") {
                this.modbusAddrDefaultValue[char2MbusAddr] = "";
              } else {
                this.modbusAddrDefaultValue[char2MbusAddr] =
                  param[ParamField.default];
              }
            } else {
              this.modbusAddrLength[char2MbusAddr]++;
              if (param[ParamField.default].trim() !== "") {
                this.modbusAddrDefaultValue[char2MbusAddr] +=
                  param[ParamField.default];
              }
            }
            break;
          default:
            this.modbusAddrLength[param[ParamField.mbusAddress]] = 0;
            this.modbusAddrDefaultValue[param[ParamField.mbusAddress]] = 0;
        }
      }
    },

    initWriteableParams() {
      this.quickParams = [];
      this.writeableParams = [];
      for (let i = 0; i < this.params.length; i++) {
        const param = this.params[i];
        if (param[ParamField.mbusAddress] === "") {
          continue;
        }
        this.selectedQuickParams[param[ParamField.mbusAddress]] ??= false;
        if (
          param[ParamField.inputType] !== "checkbox" &&
          param[ParamField.type] !== "char2" &&
          parseInt(param[ParamField.mbusAddress]) > 9 &&
          getParamWriteAccessLevel(param) === 1
        ) {
          this.quickParams.push(param);
        }

        if (this.isParamUserWriteable(param)) {
          this.writeableParams.push(param);
        }
      }
    },

    saveModbusValue(reply: number[]) {
      // Special 1: these need to be saved while params.csv is not yet loaded
      if (
        reply[0] === softwareVersionAddr ||
        reply[0] === hardwareVersionAddr ||
        reply[0] === parameterVersionAddr
      ) {
        // Legacy versions will return:
        // [0, 0, 0, 39, 17]
        // [2, 0, 0, 117, 48]
        // [4, 0, 0, 39, 16]
        const newVersion = bcdArrayToVersionString(reply.slice(1, 5));
        this.modbusAddrValue[reply[0]] = newVersion;
        if (reply[0] === parameterVersionAddr) {
          apiVersion.value = newVersion;
        }
        return;
      }
      // Special 2: this one needs to be saved while params.csv is not yet loaded
      if (reply[0] === modbusAddress.identification) {
        let str = "";
        for (let i = 1; i < reply.length; i++) {
          if (reply[i] !== 0) {
            str = str.concat(String.fromCharCode(reply[i]));
          }
        }
        this.modbusAddrValue[reply[0]] = str;
        return;
      }
      // Regular way: params.csv and modbusAddrParams are known.
      if (!(reply[0] in this.modbusAddrParams)) {
        return;
      }
      switch (this.modbusAddrParams[reply[0]].type) {
        //---------- bool -----------------------
        case "bool":
          if (reply.length === 3) {
            this.modbusAddrValue[reply[0]] = reply[2] !== 0;
          }
          break;
        //---------- uint16_t -----------------------
        case "uint16_t":
          if (reply.length === 3) {
            this.modbusAddrValue[reply[0]] = bytes_to_uint16([
              reply[1],
              reply[2],
            ]);
          }
          break;
        //---------- uint32_t -----------------------
        case "uint32_t":
          if (reply.length === 5) {
            this.modbusAddrValue[reply[0]] = bytes_to_uint32([
              reply[1],
              reply[2],
              reply[3],
              reply[4],
            ]);
          }
          break;
        //---------- int -----------------------
        case "int":
          if (reply.length === 5) {
            this.modbusAddrValue[reply[0]] = bytes_to_int32([
              reply[1],
              reply[2],
              reply[3],
              reply[4],
            ]);
          }
          break;
        //---------- float -----------------------
        case "float":
          if (reply.length === 5) {
            this.modbusAddrValue[reply[0]] = parseFloat(
              bytes_to_float([reply[1], reply[2], reply[3], reply[4]]).toFixed(
                5,
              ),
            );
          }
          break;
        //---------- char2 -----------------------
        case "char2":
          let str = "";
          for (let i = 1; i < reply.length; i++) {
            if (reply[i] !== 0) {
              str = str.concat(String.fromCharCode(reply[i]));
            }
          }
          this.modbusAddrValue[reply[0]] = str;
          break;
      }
    },

    // open serial port -> webworker
    async openSerial(
      portIndex: number,
      baud: number,
      deviceAddr: number,
    ): Promise<boolean> {
      this.portState = PortState.connecting;
      try {
        const replyOpenPort = await modbusWorker.postMsgOpenPort(
          portIndex,
          baud,
        );
        if (replyOpenPort) {
          this.deviceAddress = deviceAddr;
          this.portIndex = portIndex;
        }
      } catch (error) {
        $log?.error("Error during openSerial()");
      }
      const replyGetPortState = await modbusWorker.postMsgGetPortState();
      if (replyGetPortState) {
        this.portState = PortState.connected;
        this.portIndex = portIndex;
        return true;
      } else {
        this.portState = PortState.disconnected;
        this.portIndex = -1;
        return false;
      }
    },

    /**
     * Closes the serial port. Uses a 1500ms timeout to handle stuck workers.
     * @see resetModbusWorker - terminates the worker if it doesn't respond
     * @returns true if disconnected (or forced), false if still connected
     */
    async closeSerial(): Promise<boolean> {
      this.portState = PortState.connecting;
      const timeoutMs = 1500;
      try {
        await Promise.race([
          modbusWorker.postMsgClosePort(),
          timeout(timeoutMs),
        ]);
        const replyGetPortState = await Promise.race([
          modbusWorker.postMsgGetPortState(),
          timeout(timeoutMs),
        ]);
        if (replyGetPortState) {
          this.portState = PortState.connected;
          return false;
        }
      } catch (error) {
        // Timeout or error: terminate stuck worker and create new one
        resetModbusWorker();
        modbusWorker = getModbusWorker();
      }
      this.portState = PortState.disconnected;
      this.portIndex = -1;
      return true;
    },

    isParamUserReadable(param: ModbusParam) {
      return this.accessLevel >= getParamReadAccessLevel(param);
    },

    isParamUserWriteable(param: ModbusParam) {
      return this.accessLevel >= getParamWriteAccessLevel(param);
    },

    isAddressUserReadable(modbusAddress: string): boolean {
      const foundParam = this.params.find(
        (param) => param[ParamField.mbusAddress] === modbusAddress,
      );
      return foundParam ? this.isParamUserReadable(foundParam) : false;
    },

    isAddressUserWriteable(modbusAddress: string): boolean {
      const foundParam = this.params.find(
        (param) => param[ParamField.mbusAddress] === modbusAddress,
      );
      return foundParam ? this.isParamUserWriteable(foundParam) : false;
    },

    // read modbus data
    // from device -> to modbusAddrValue
    async modbusRead(
      deviceAddr: number,
      modbusAddrX: number | string,
      counts: number /* count of register(16bits) */,
    ) {
      if (this.portState !== PortState.connected) {
        $log?.error("Cannot read: port is not connected");
        return;
      }
      const modbusAddr = parseInt(modbusAddrX.toString());
      for (let i = 0; i < counts; i++) {
        this.modbusAddrReadBusy[modbusAddr + i] = true;
      }
      this.modbusAddrValue[modbusAddr] = null;
      const reply: number[] = await modbusWorker.postMsgModbusRead(
        deviceAddr,
        modbusAddr,
        counts,
      );

      if (reply.length > 1) {
        if (reply[0] === modbusAddress.accessLevel) {
          switch (reply[2]) {
            case AccessLevelSetting.manufacturer:
              this.accessLevel = AccessLevel.manufacturer;
              break;
            case AccessLevelSetting.full:
              this.accessLevel = AccessLevel.full;
              break;
            case AccessLevelSetting.advanced:
              this.accessLevel = AccessLevel.advanced;
              break;
            case AccessLevelSetting.normal:
            default:
              this.accessLevel = AccessLevel.normal;
          }
        } else {
          this.saveModbusValue(reply);

          for (let i = 1; i < reply.length; i += 2) {
            this.modbusValues[reply[0] + (i - 1) / 2] = bytes_to_uint16([
              reply[i],
              reply[i + 1],
            ]);
          }

          if (
            reply[0] >= modbusAddress.logDay0DateTime &&
            reply[0] <= modbusAddress.logDay6DateTime
          ) {
            this.logDateState++;
          }
        }
        if (this.communicationScore < 10) {
          this.communicationScore++;
        }
        for (let i = 0; i < counts; i++) {
          this.modbusAddrErrMsg[modbusAddr + i] = "";
        }
      } else {
        if (this.communicationScore > 0) {
          this.communicationScore--;
        }
        if (this.communicationScore === 0) {
          for (let i = 0; i < counts; i++) {
            this.modbusValues[modbusAddr + i] = 0;
          }
        }
        for (let i = 0; i < counts; i++) {
          this.modbusAddrErrMsg[modbusAddr + i] = "Read Error. Please reload!";
        }
        $log?.error(`Read error addr: ${modbusAddr}, counts: ${counts}`);
      }
      for (let i = 0; i < counts; i++) {
        this.modbusAddrReadBusy[modbusAddr + i] = false;
      }
    },

    // write modbus values
    // from modbusAddrValue -> to device
    async modbusWrite(
      deviceAddr: number,
      modbusAddrX: number | string,
      counts: number,
      values: Uint8Array,
    ) {
      if (this.portState !== PortState.connected) {
        $log?.error(`Cannot write to ${modbusAddrX}: port is not connected`);
        return;
      }
      const modbusAddr = parseInt(modbusAddrX.toString());
      for (let i = 0; i < counts; i++) {
        this.modbusAddrWriteBusy[modbusAddr + i] = true;
      }
      const reply: number[] = await modbusWorker.postMsgModbusWrite(
        deviceAddr,
        modbusAddr,
        counts,
        values,
      );

      if (reply.length > 1) {
        if (reply[1] == 0x01) {
          for (let i = 0; i < counts; i++) {
            this.modbusAddrErrMsg[modbusAddr + i] = "";
          }
          this.modbusIsChanged = true;
          this.modbusRead(deviceAddr, modbusAddr, counts);
        }
      } else {
        for (let i = 0; i < counts; i++) {
          this.modbusAddrErrMsg[modbusAddr + i] =
            "Write Error. Please rewrite!";
        }
        $log?.error(
          `write error addr: ${modbusAddr} counts: ${counts} values: ${values}`,
        );
      }
      for (let i = 0; i < counts; i++) {
        this.modbusAddrWriteBusy[modbusAddr + i] = false;
      }
    },

    getShowValueWithConverter(
      modbusAddr: number,
      converter: (input: number[]) => number,
    ): number | null {
      if (
        isNullOrUndefined(this.modbusValues[modbusAddr]) ||
        isNullOrUndefined(this.modbusValues[modbusAddr + 1])
      ) {
        return null;
      } else {
        const byte0 = uint16_to_bytes(this.modbusValues[modbusAddr]);
        const byte1 = uint16_to_bytes(this.modbusValues[modbusAddr + 1]);
        return converter([byte0[0], byte0[1], byte1[0], byte1[1]]);
      }
    },

    // getting the value to show from modbusValues
    getShowValue(modbusAddr: number, _type: DataType | "0" | null) {
      switch (_type) {
        case "float":
          return this.getShowValueWithConverter(modbusAddr, bytes_to_float);
        case "int":
          return this.getShowValueWithConverter(modbusAddr, bytes_to_int32);
        case "uint32_t":
          return this.getShowValueWithConverter(modbusAddr, bytes_to_uint32);
        case "uint16_t":
          if (isNullOrUndefined(this.modbusValues[modbusAddr])) {
            return null;
          } else {
            return this.modbusValues[modbusAddr];
          }
        case "bool":
          if (isNullOrUndefined(this.modbusValues[modbusAddr])) {
            return null;
          }
          return this.modbusValues[modbusAddr] != 0;
        default:
          return undefined;
      }
    },

    // convert the value from fromUnit unit to toUnit
    convert(
      value: number,
      fromUnit: string | undefined,
      toUnit: string | undefined,
    ): number {
      if (fromUnit === undefined || toUnit === undefined) {
        return value;
      }
      if (fromUnit === toUnit) {
        return value;
      }
      const from_unit = this.guiUnits.find((item) => item.name === fromUnit);
      const to_unit = this.guiUnits.find((item) => item.name === toUnit);
      const base = from_unit
        ? value * parseFloat(from_unit?.factor) + parseFloat(from_unit?.offset)
        : value;
      return to_unit
        ? (base - parseFloat(to_unit.offset)) / parseFloat(to_unit.factor)
        : base;
    },

    // get polling data from device
    pollingData(deviceAddr: number) {
      if (this.portState !== PortState.connected) {
        $log?.error("Cannot poll data: port is not connected");
        return;
      }
      modbusWorker
        .postMsgModbusPollingData(deviceAddr)
        .then((reply: number[]) => {
          if (reply.length <= 1) {
            $log?.error("pollingData: Error: got empty response");
            return;
          }
          if (reply[0] === this.deviceAddress && reply[1] === 0x41) {
            const date = bytes_to_date_time(reply.slice(2, 8)).getTime();

            this.labelSeries.pollingData.push(date);
            const pushData = (serieName: SensorCode, val: number) => {
              this.dataSeries[serieName].pollingData.push([date, val]);
            };

            const iv = bytes_to_float(reply.slice(28, 32));
            const ev = bytes_to_float(reply.slice(32, 36));

            pushData("RS", bytes_to_float(reply.slice(8, 12)));
            pushData("ISA", bytes_to_float(reply.slice(12, 16)));
            pushData("ISB", bytes_to_float(reply.slice(16, 20)));
            pushData("ISC", bytes_to_float(reply.slice(20, 24)));
            pushData("SP", bytes_to_float(reply.slice(24, 28)));
            pushData("IV", iv);
            pushData("EV", ev);
            pushData("IEV", iv - ev);
            pushData("DT", bytes_to_float(reply.slice(36, 40)));

            this.pollingFlag = !this.pollingFlag;
          }
        });
    },

    // get log date from device
    getLogDate() {
      if (this.portState !== PortState.connected) {
        $log?.error("Cannot get log date: port is not connected");
        return;
      }
      this.logDateState = 0;
      const logDateAddress = modbusAddress.logDate;
      for (let i = 0; i < 7; i++) {
        this.modbusRead(this.deviceAddress, logDateAddress + i * 2, 2);
      }
    },
    // clear data
    clearLog1mData() {
      this.log1mDataTime = [];
      this.log1mDataRP = [];
      this.log1mDataS1 = [];
      this.log1mDataS2 = [];
      this.log1mDataM12 = [];
      this.log1mDataSetpoint = [];
      this.log1mDataV1 = [];
      this.log1mDataV2 = [];
      this.log1mDataTemp = [];
    },
    clearLog1sData() {
      this.log1sDataTime = [];
      this.log1sDataRP = [];
      this.log1sDataS1 = [];
      this.log1sDataS2 = [];
      this.log1sDataM12 = [];
      this.log1sDataSetpoint = [];
      this.log1sDataV1 = [];
      this.log1sDataV2 = [];
      this.log1sDataTemp = [];
    },
    clearLog100msData() {
      this.log100msDataTime = [];
      this.log100msDataRP = [];
      this.log100msDataS1 = [];
      this.log100msDataS2 = [];
      this.log100msDataM12 = [];
      this.log100msDataSetpoint = [];
      this.log100msDataV1 = [];
      this.log100msDataV2 = [];
      this.log100msDataTemp = [];
    },
    // reading log data at 1-minute intervals
    readLog1mData(deviceAddr: number, dt_start: number, dt_end: number) {
      if (this.portState !== PortState.connected) {
        $log?.error("Cannot read log 1m data: port is not connected");
        return;
      }
      this.log1mDataIsOk = false;
      this.log1mDataBusy = true;
      modbusWorker
        .postMsgModbusLogData(deviceAddr, dt_start, dt_end, "minute")
        .then((reply: number[]) => {
          if (reply.length <= 1) {
            $log?.error("readLog1mData: Error: got empty response");
          } else {
            const count = Math.floor((reply.length - 4) / 38);
            if (
              reply[0] == this.deviceAddress &&
              reply[1] == 0x42 &&
              count == Math.floor((dt_end - dt_start) / 60000) + 1
            ) {
              for (let i = 0; i < count; i++) {
                const pos = i * 38 + 2;
                if (reply[pos] != 255) {
                  // this.log1mDataTime.push(
                  //   bytes_to_date_time(reply.slice(pos, pos + 6)).getTime()
                  // );
                  this.log1mDataTime.push(
                    dt_start + i * 60 * millisecondsInSecond,
                  );
                  this.log1mDataRP.push(
                    bytes_to_float(reply.slice(pos + 6, pos + 10)),
                  );
                  this.log1mDataS1.push(
                    bytes_to_float(reply.slice(pos + 10, pos + 14)),
                  );
                  this.log1mDataS2.push(
                    bytes_to_float(reply.slice(pos + 14, pos + 18)),
                  );
                  this.log1mDataM12.push(
                    bytes_to_float(reply.slice(pos + 18, pos + 22)),
                  );
                  this.log1mDataSetpoint.push(
                    bytes_to_float(reply.slice(pos + 22, pos + 26)),
                  );
                  this.log1mDataV1.push(
                    bytes_to_float(reply.slice(pos + 26, pos + 30)),
                  );
                  this.log1mDataV2.push(
                    bytes_to_float(reply.slice(pos + 30, pos + 34)),
                  );
                  this.log1mDataTemp.push(
                    bytes_to_float(reply.slice(pos + 34, pos + 38)),
                  );
                }
              }
              this.log1mDataIsOk = true;
            }
          }
          this.log1mDataBusy = false;
        });
    },
    // reading log data at 1-second intervals
    readLog1sData(deviceAddr: number, dt_start: number, dt_end: number) {
      if (this.portState !== PortState.connected) {
        $log?.error("Cannot read log 1s data: port is not connected");
        return;
      }
      this.log1sDataIsOk = false;
      this.log1sDataBusy = true;
      modbusWorker
        .postMsgModbusLogData(deviceAddr, dt_start, dt_end, "second")
        .then((reply: number[]) => {
          if (reply.length <= 1) {
            $log?.error("readLog1sData: Error: got empty response");
          } else {
            const count = Math.floor((reply.length - 4) / 38);
            if (
              reply[0] == this.deviceAddress &&
              reply[1] == 0x42 &&
              count ==
                Math.floor((dt_end - dt_start) / millisecondsInSecond) + 1
            ) {
              for (let i = 0; i < count; i++) {
                const pos = i * 38 + 2;
                if (reply[pos] != 255) {
                  // this.log1sDataTime.push(
                  //   bytes_to_date_time(reply.slice(pos, pos + 6)).getTime()
                  // );
                  this.log1sDataTime.push(dt_start + i * millisecondsInSecond);
                  this.log1sDataRP.push(
                    bytes_to_float(reply.slice(pos + 6, pos + 10)),
                  );
                  this.log1sDataS1.push(
                    bytes_to_float(reply.slice(pos + 10, pos + 14)),
                  );
                  this.log1sDataS2.push(
                    bytes_to_float(reply.slice(pos + 14, pos + 18)),
                  );
                  this.log1sDataM12.push(
                    bytes_to_float(reply.slice(pos + 18, pos + 22)),
                  );
                  this.log1sDataSetpoint.push(
                    bytes_to_float(reply.slice(pos + 22, pos + 26)),
                  );
                  this.log1sDataV1.push(
                    bytes_to_float(reply.slice(pos + 26, pos + 30)),
                  );
                  this.log1sDataV2.push(
                    bytes_to_float(reply.slice(pos + 30, pos + 34)),
                  );
                  this.log1sDataTemp.push(
                    bytes_to_float(reply.slice(pos + 34, pos + 38)),
                  );
                }
              }
              this.log1sDataIsOk = true;
            }
          }
          this.log1sDataBusy = false;
        });
    },
    // reading log data at 100-millisecond intervals
    readLog100msData(deviceAddr: number, dt_start: number, dt_end: number) {
      if (this.portState !== PortState.connected) {
        $log?.error("Cannot read log 100ms data: port is not connected");
        return;
      }
      this.log100msDataIsOk = false;
      this.log100msDataBusy = true;
      modbusWorker
        .postMsgModbusLogData(deviceAddr, dt_start, dt_end, "ms")
        .then((reply: number[]) => {
          if (reply.length <= 1) {
            $log?.error("readLog100msData: Error: got empty response");
          } else {
            const count = Math.floor((reply.length - 4) / 38);
            if (
              reply[0] == this.deviceAddress &&
              reply[1] == 0x42 &&
              count == Math.floor((dt_end - dt_start) / 100) + 1
            ) {
              for (let i = 0; i < count; i++) {
                const pos = i * 38 + 2;
                if (reply[pos] != 255) {
                  // this.log100msDataTime.push(
                  //   bytes_to_date_time(reply.slice(pos, pos + 6)).getTime()
                  // );
                  this.log100msDataTime.push(dt_start + i * 100);
                  this.log100msDataRP.push(
                    bytes_to_float(reply.slice(pos + 6, pos + 10)),
                  );
                  this.log100msDataS1.push(
                    bytes_to_float(reply.slice(pos + 10, pos + 14)),
                  );
                  this.log100msDataS2.push(
                    bytes_to_float(reply.slice(pos + 14, pos + 18)),
                  );
                  this.log100msDataM12.push(
                    bytes_to_float(reply.slice(pos + 18, pos + 22)),
                  );
                  this.log100msDataSetpoint.push(
                    bytes_to_float(reply.slice(pos + 22, pos + 26)),
                  );
                  this.log100msDataV1.push(
                    bytes_to_float(reply.slice(pos + 26, pos + 30)),
                  );
                  this.log100msDataV2.push(
                    bytes_to_float(reply.slice(pos + 30, pos + 34)),
                  );
                  this.log100msDataTemp.push(
                    bytes_to_float(reply.slice(pos + 34, pos + 38)),
                  );
                }
              }
              this.log100msDataIsOk = true;
            }
          }
          this.log100msDataBusy = false;
        });
    },

    // generate random data for simulation
    generateRandomData() {
      for (const serieName of allSensorCodes) {
        if (this.dataSeries[serieName].demoData.length >= this.maxTime * 10) {
          this.dataSeries[serieName].demoData.splice(0, 1);
        }
      }

      const date = Date.now();

      this.labelSeries.demoData.push(date);
      const pushData = (serieName: SensorCode, val: number) => {
        this.dataSeries[serieName].demoData.push([date, val]);
      };

      const iv = Math.random() > 0.5 ? 1 : 0;
      const ev = 1 - iv;

      pushData("RS", Math.random() * 10 + 40);
      pushData("ISA", Math.random() * 10 + 40);
      pushData("ISB", Math.random() * 10 + 40);
      pushData("ISC", Math.random() * 10 + 40);
      pushData("SP", 45);
      pushData("IV", iv);
      pushData("EV", ev);
      pushData("IEV", iv - ev);
      pushData("DT", Math.random() * 3 + 30);

      this.demoFlag = !this.demoFlag;
    },
  },
});
